import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Menu, X, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const C = { primary: "#3B1E9C", dark: "#310A42", green: "#2E7D32", soft: "#F7F6FB", text: "#2B2233", muted: "#5F5868" };
const NAV = [{ id: "chi-sono", l: "Chi sono" }, { id: "come-lavoro", l: "Come lavoro" }, { id: "servizi", l: "Servizi" }];
const STEPS = [
  { t: "Ascolto e valutazione", d: "Partiamo dalle tue esigenze e osserviamo il movimento, considerando le possibili cause del dolore." },
  { t: "Consapevolezza muscolare", d: "Lavoriamo sulla percezione, sul controllo muscolare e sull'attivazione segmentale." },
  { t: "Mobilità e rilascio", d: "Introduciamo esercizi di mobilità e tecniche di rilascio miofasciale adeguate al percorso." },
  { t: "Forza globale", d: "Costruiamo gradualmente forza con esercizi di rinforzo muscolare globale." },
  { t: "Postura e continuità", d: "Ricevi indicazioni posturali e strumenti per mantenere i progressi e aiutare a prevenire le ricadute." },
];
const SERVICES = [
  { img: "/home/svc1.jpg", t: "Percorsi per il dolore cronico", d: "Esercizi personalizzati per lavorare sul movimento e sulla gestione del dolore nel quotidiano." },
  { img: "/home/svc2.jpg", t: "Recupero post intervento o trauma", d: "Un ritorno graduale al movimento attraverso attività adattate alla tua situazione." },
  { img: "/home/svc3.jpg", t: "Prevenzione", d: "Un lavoro su forza, mobilità e abitudini posturali per prenderti cura di te nel tempo." },
  { img: "/home/svc4.jpg", t: "Coaching online", d: "Un programma individuale da svolgere in autonomia, con un controllo mensile per monitorare i progressi e adattare il percorso." },
];
const RESULTS = ["Ridurre il dolore", "Recuperare la mobilità", "Rafforzare e tonificare i muscoli", "Migliorare la postura", "Aiutare a prevenire la ricomparsa del dolore"];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function Tag({ children, light }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: light ? "#C9B8FF" : C.green }}>{children}</div>;
}

function H2({ children, light }) {
  return <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight" style={{ color: light ? "#FFFFFF" : C.dark }}>{children}</h2>;
}

function BtnPrimary({ children, onClick, testid, type = "button", disabled }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testid}
      className="rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
      style={{ background: C.primary }}>{children}</button>
  );
}

function BtnOutline({ children, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="rounded-full px-6 py-3 text-sm font-semibold border transition-colors duration-200 hover:bg-[#3B1E9C] hover:text-white"
      style={{ borderColor: C.dark, color: C.dark }}>{children}</button>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const go = (id) => { setOpen(false); setTimeout(() => scrollTo(id), 50); };
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-black/5" data-testid="home-navbar">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="home-logo">
          <img src="/logo.png" alt="Sara Absi" className="h-9 sm:h-10 w-auto" />
        </button>
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map(n => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}`} className="text-sm font-medium hover:text-[#3B1E9C] transition-colors" style={{ color: C.text }}>{n.l}</button>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-5">
          <Link to="/login" data-testid="nav-login" className="text-sm font-medium hover:text-[#3B1E9C] transition-colors" style={{ color: C.muted }}>Area clienti</Link>
          <BtnPrimary onClick={() => go("contatti")} testid="nav-contact-btn">Contattami</BtnPrimary>
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="home-menu-toggle" style={{ color: C.dark }} aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-black/5 bg-white px-5 py-4 flex flex-col gap-4" data-testid="home-mobile-menu">
          {NAV.map(n => <button key={n.id} onClick={() => go(n.id)} data-testid={`mnav-${n.id}`} className="text-left font-medium" style={{ color: C.text }}>{n.l}</button>)}
          <Link to="/login" data-testid="mnav-login" className="font-medium" style={{ color: C.muted }}>Area clienti</Link>
          <BtnPrimary onClick={() => go("contatti")} testid="mnav-contact-btn">Contattami</BtnPrimary>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 md:py-24 grid md:grid-cols-2 gap-12 items-center" data-testid="home-hero">
      <div className="home-reveal">
        <Tag>Movimento su misura, persona al centro</Tag>
        <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" style={{ color: C.dark }} data-testid="hero-title">
          <span className="block font-normal tracking-wide text-6xl sm:text-7xl lg:text-8xl leading-[0.95]" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.primary }} data-testid="hero-title-name">Sara Absi</span>
          Ritrova fiducia nel movimento.
        </h1>
        <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-lg" style={{ color: C.muted }}>
          Percorsi personalizzati di rieducazione motoria per prenderti cura del movimento, della postura e della tua autonomia, un passo alla volta.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <BtnPrimary onClick={() => scrollTo("servizi")} testid="hero-discover-btn">Scopri il tuo percorso</BtnPrimary>
          <BtnOutline onClick={() => scrollTo("contatti")} testid="hero-contact-btn">Contattami</BtnOutline>
        </div>
      </div>
      <div className="relative home-reveal [animation-delay:150ms] mx-auto w-full max-w-md md:max-w-none">
        <div className="absolute -top-6 -right-4 w-28 h-28 rounded-full" style={{ background: "#ECE6FA" }} />
        <img src="/home/hero.jpg" alt="Sara Absi durante un allenamento" data-testid="hero-image"
          className="relative w-full aspect-[7/8] object-cover rounded-2xl shadow-xl" />
        <div className="absolute -bottom-3 -left-3 w-20 h-8 rounded-full" style={{ background: C.green }} />
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="chi-sono" className="scroll-mt-16 py-16 md:py-28" style={{ background: C.soft }} data-testid="home-about">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <img src="/home/chisono.jpg" alt="Sara Absi con una cliente" data-testid="about-image"
          className="w-full max-w-md mx-auto md:mx-0 aspect-[4/5] object-cover rounded-2xl shadow-lg" />
        <div>
          <Tag>Chi sono</Tag>
          <H2>Un percorso che parte da te.</H2>
          <p className="mt-5 leading-relaxed" style={{ color: C.muted }}>
            Sono Sara Absi, specialista in Scienze Motorie e laureata in Scienze e Tecniche delle Attività Motorie Preventive e Adattate. Mi occupo di rieducazione motoria attraverso percorsi di esercizio costruiti sulle esigenze di ogni persona.
          </p>
          <p className="mt-4 leading-relaxed" style={{ color: C.muted }}>
            Ascoltare come ti muovi, comprendere le tue difficoltà e trovare insieme un modo sostenibile per progredire: è da qui che inizia il lavoro.
          </p>
        </div>
      </div>
    </section>
  );
}

function Method() {
  return (
    <section id="come-lavoro" className="scroll-mt-16 py-16 md:py-28 bg-white" data-testid="home-method">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-2 gap-6 md:gap-16 items-end mb-12">
          <div><Tag>Come lavoro</Tag><H2>Ogni movimento ha il suo punto di partenza.</H2></div>
          <p className="leading-relaxed" style={{ color: C.muted }}>
            Il percorso parte da una valutazione delle esigenze individuali e delle possibili cause del dolore. Gli esercizi vengono poi adattati ai tuoi obiettivi e ai progressi nel tempo.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.t} data-testid={`method-card-${i + 1}`}
              className={`rounded-2xl border border-black/5 bg-white p-6 shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-md ${i === 4 ? "lg:col-span-2" : ""}`}>
              <div className="text-xs font-semibold" style={{ color: C.green }}>{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-3 text-lg font-bold" style={{ color: C.dark }}>{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: C.muted }}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="servizi" className="scroll-mt-16 py-16 md:py-28" style={{ background: C.soft }} data-testid="home-services">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Tag>Servizi</Tag>
        <H2>Il percorso giusto per le tue esigenze.</H2>
        <p className="mt-4 mb-10" style={{ color: C.muted }}>Non esiste una soluzione uguale per tutti. Scegliamo insieme da dove cominciare.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => (
            <div key={s.t} data-testid={`service-card-${i + 1}`} className="rounded-2xl overflow-hidden bg-white shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
              <img src={s.img} alt={s.t} className="w-full aspect-[4/3] object-cover" />
              <div className="p-5">
                <h3 className="text-lg font-bold leading-snug" style={{ color: C.dark }}>{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.muted }}>{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Results() {
  return (
    <section className="py-16 md:py-28 bg-white" data-testid="home-results">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-10 md:gap-16">
        <div>
          <Tag>Risultati</Tag>
          <H2>Più possibilità di muoverti, ogni giorno.</H2>
          <p className="mt-5 leading-relaxed" style={{ color: C.muted }}>Gli obiettivi del percorso si definiscono insieme e possono evolvere con le tue esigenze.</p>
        </div>
        <ul className="divide-y divide-black/10">
          {RESULTS.map((r, i) => (
            <li key={r} data-testid={`result-item-${i + 1}`} className="py-4 flex items-center gap-3 font-semibold" style={{ color: C.dark }}>
              <Check size={16} style={{ color: C.green }} className="shrink-0" /> {r}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ContactForm() {
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) { toast.error("Compila tutti i campi"); return; }
    setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success("Richiesta inviata! Ti risponderò al più presto.");
      setF({ name: "", email: "", message: "" });
    } catch (err) { toast.error(err?.response?.status === 422 ? "Indirizzo email non valido" : formatApiError(err)); }
    finally { setBusy(false); }
  };
  const field = "mt-1.5 w-full rounded-lg border border-black/10 px-3 py-2.5 text-[#2B2233] bg-white outline-none focus:border-[#3B1E9C] focus:ring-2 focus:ring-[#3B1E9C]/15 transition";
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white p-6 sm:p-8 shadow-2xl space-y-4" data-testid="contact-form">
      <label className="block text-sm font-semibold" style={{ color: C.dark }}>Nome
        <input data-testid="contact-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className={field} />
      </label>
      <label className="block text-sm font-semibold" style={{ color: C.dark }}>Email
        <input data-testid="contact-email" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className={field} />
      </label>
      <label className="block text-sm font-semibold" style={{ color: C.dark }}>Messaggio
        <textarea data-testid="contact-message" rows={5} value={f.message} onChange={e => setF({ ...f, message: e.target.value })} className={field} />
      </label>
      <div className="[&>button]:w-full"><BtnPrimary type="submit" testid="contact-submit" disabled={busy}>{busy ? "Invio in corso…" : "Invia la richiesta"}</BtnPrimary></div>
    </form>
  );
}

function Contact() {
  return (
    <section id="contatti" className="scroll-mt-16 py-16 md:py-28" style={{ background: C.dark }} data-testid="home-contact">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <div>
          <Tag light>Facciamo il primo passo</Tag>
          <H2 light>Parliamo del tuo percorso.</H2>
          <p className="mt-5 leading-relaxed text-white/75 max-w-md">Raccontami cosa stai cercando. Possiamo partire da una semplice richiesta di informazioni e capire insieme come procedere.</p>
        </div>
        <ContactForm />
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10" style={{ background: C.dark }} data-testid="home-footer">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-white/80">
        <div data-testid="footer-address">
          <div className="font-bold text-white">Sara Absi</div>
          <div>Aprilia (LT), Via Alcide De Gasperi 58</div>
        </div>
        <div className="flex flex-col sm:items-end gap-1">
          <a href="mailto:info@saraabsi.it" data-testid="footer-email" className="font-semibold text-white underline underline-offset-4 hover:text-[#C9B8FF] transition-colors">info@saraabsi.it</a>
          <Link to="/login" data-testid="footer-login" className="text-xs text-white/60 hover:text-white transition-colors">Area clienti</Link>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="home-page min-h-screen bg-white overflow-x-hidden" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: C.text }} data-testid="landing-page">
      <style>{`.home-reveal{animation:homeUp .7s ease-out both}@keyframes homeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}`}</style>
      <Navbar />
      <Hero />
      <About />
      <Method />
      <Services />
      <Results />
      <Contact />
      <Footer />
    </div>
  );
}
